import { format } from 'date-fns-tz';
import { useLoaderData, useSearchParams } from 'react-router';
import { z } from 'zod';
import { zx } from 'zodix';

import { EmptySetMessage } from '~/components/content/empty-set-message';
import { Select, SelectItem } from '~/components/ui/select';
import { type AthleteGymClassListing } from '~/domain/gym-classes/models/gym-class';
import { getAthleteGymClassOccurrences } from '~/domain/gym-classes/queries/get-athlete-gym-class-occurrences.server';
import { getAthleteGymClasses } from '~/domain/gym-classes/queries/get-athlete-gym-classes.server';
import { parseDate, parseTime } from '~/domain/shared/time-helpers';
import { getSessionIfAuthenticated } from '~/utils/auth.server';
import { handleDomainError } from '~/utils/errors.server';
import { getGymIdAndSlugFromParams } from '~/utils/gyms.server';

import { RsvpButton } from '../components/rsvp-button';

import { type Route } from './+types/_index';

export async function loader({ request, params }: Route.LoaderArgs) {
  const session = await getSessionIfAuthenticated(request);

  // Get gym classes
  const { gymId } = await getGymIdAndSlugFromParams(params);
  const gymClassesResult = await getAthleteGymClasses(gymId);
  const gymClasses = gymClassesResult.mapErr(handleDomainError).unwrap();

  // If there are no classes, there's nothing to show
  if (gymClasses.length < 1) {
    return {
      gymId,
      defaultClassId: null,
      gymClasses: [] as AthleteGymClassListing[],
      gymClassOccurrences: null,
    };
  }

  // Get upcoming classes for the selected class (or first one if none selected)
  const query = zx.parseQuerySafe(request, {
    gymClassId: z.coerce.number(),
  });
  const defaultClassId = gymClasses[0].gymClassId;
  const gymClassId = query.success ? query.data.gymClassId : defaultClassId;
  const result = await getAthleteGymClassOccurrences(gymClassId, session?.userId);
  const gymClassOccurrences = result.mapErr(handleDomainError).unwrap();

  return {
    gymId,
    defaultClassId,
    gymClasses,
    gymClassOccurrences,
  };
}

const formatClassDuration = (durationMs: number) => {
  const minutes = durationMs / 60 / 1000;
  return `${minutes} min`;
};

const getDefaultSearchParams = (gymClassId: number | null) => {
  const defaultSearchParams = new URLSearchParams();
  if (gymClassId != null) {
    defaultSearchParams.set('gymClassId', gymClassId.toString());
  }
  return defaultSearchParams;
};

export default function Classes() {
  const { gymId, defaultClassId, gymClasses, gymClassOccurrences } = useLoaderData<typeof loader>();

  const [searchParams, setSearchParams] = useSearchParams(getDefaultSearchParams(defaultClassId));

  const handleSelectClassId = (gymClassId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('gymClassId', gymClassId);
    setSearchParams(newParams);
  };

  const selectedGymClassId = searchParams.get('gymClassId');
  const selectedGymClass = gymClasses.find(
    (gymClass) => gymClass.gymClassId.toString() === selectedGymClassId,
  );

  return (
    <div className="flex flex-col items-center">
      {gymClassOccurrences != null ? (
        <>
          <div className="w-full rounded-lg bg-gray-200 p-6">
            <Select
              className="h-[unset] bg-transparent"
              onValueChange={handleSelectClassId}
              value={searchParams.get('gymClassId') ?? undefined}
            >
              {gymClasses.map((gymClass) => (
                <SelectItem key={gymClass.gymClassId} value={gymClass.gymClassId.toString()}>
                  <div className="flex w-[calc(100vw-110px)] max-w-[370px] items-center justify-between text-body2">
                    <span className="text-balance text-start">{gymClass.gymClassName}</span>
                    {gymClass.instructorFirstName || gymClass.instructorLastName ? (
                      <span className="text-nowrap italic text-gray-900">
                        {gymClass.instructorFirstName} {gymClass.instructorLastName}
                      </span>
                    ) : null}
                  </div>
                </SelectItem>
              ))}
            </Select>
          </div>
          <div className="w-full">
            {gymClassOccurrences.occurrences.map((occurrence) => (
              <div
                key={occurrence.id}
                className="grid grid-cols-4 grid-rows-2 gap-2 border-b-2 border-b-gray-200 py-4 font-mono text-subtitle2 font-extrabold text-primary"
              >
                <div>{format(parseDate(occurrence.date), 'EEEE').toUpperCase()}</div>
                <div>{format(parseTime(occurrence.startTime), 'h:mm a')}</div>
                <div>{/* Belts */}</div>
                <div className="row-span-2 w-full justify-self-center">
                  <RsvpButton
                    gymClassName={selectedGymClass?.gymClassName ?? ''}
                    gymId={gymId}
                    gymClassId={occurrence.gymClassId}
                    date={occurrence.date}
                    startTime={occurrence.startTime}
                    endTime={occurrence.endTime}
                    rsvpId={occurrence.rsvpId}
                  />
                </div>
                <div>{format(parseDate(occurrence.date), 'MMM d').toUpperCase()}</div>
                <div className="font-medium text-primary-dark">
                  {formatClassDuration(occurrence.durationMs)}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <EmptySetMessage
          title="No information entered"
          titleClassName="text-subtitle1"
          message="Contact the gym or visit their website to get the latest class information."
          className="p-0 pt-6 text-center"
        />
      )}
    </div>
  );
}
